import html2canvas from 'html2canvas';

/* ------------------------------------------------------------------ */
/*  PRD.238 — DOM → PNG render util                                   */
/*                                                                    */
/*  Wraps html2canvas so callers don't depend on it directly. The     */
/*  WhatsApp media send takes a base64 payload; we return both the    */
/*  data URL (for in-browser preview) and the bare base64 payload     */
/*  (for the POST body).                                              */
/* ------------------------------------------------------------------ */

export interface RenderToPngOptions {
  /** DOM element to capture. Must already be mounted (visibility doesn't matter). */
  node: HTMLElement;
  /** Output scale (higher = sharper, larger). Defaults to 2. */
  scale?: number;
  /** Background color, used for transparent regions. Defaults to white. */
  backgroundColor?: string;
  /**
   * Preferred output format. Defaults to 'png'. Use 'jpeg' for photographic
   * cards (before/after comparisons): PNG is huge for photo content, JPEG is
   * an order of magnitude smaller for the same visual quality.
   */
  format?: 'png' | 'jpeg';
  /** JPEG quality (0..1). Only applies when format is 'jpeg'. Defaults to 0.92. */
  quality?: number;
  /**
   * If set, shrink the encoded output to fit under this byte budget. The card
   * is re-encoded as JPEG with progressively lower quality, then downscaled,
   * until it fits. Prevents the server's media-size limit from rejecting a
   * high-resolution capture (e.g. a full-res before/after pair).
   */
  maxBytes?: number;
}

export interface RenderToPngResult {
  /** "data:image/<type>;base64,…" — handy for an inline <img> preview. */
  dataUrl: string;
  /** Bare base64 payload (no data-URL prefix). */
  base64: string;
  /** MIME type of the encoded output ('image/png' or 'image/jpeg'). */
  mimeType: string;
  width: number;
  height: number;
  /** Approximate byte size of the decoded image. */
  byteSize: number;
}

/**
 * Render a DOM node to a PNG using html2canvas.
 *
 * `useCORS: true` is enabled so cross-origin images (e.g. the org logo) draw
 * cleanly when their server returns the right CORS headers. If your logo host
 * doesn't, the image will be skipped and the rest of the prescription will
 * still render — better than a hard failure.
 */
export async function renderNodeToPng(opts: RenderToPngOptions): Promise<RenderToPngResult> {
  // html2canvas captures the DOM *as currently painted*. Snapshotting too
  // early produces visible regressions vs. the print preview:
  //  - Fonts not loaded → fallback metrics → pill aspect ratios distort,
  //    `border-radius: 999` no longer clips to a pill, line breaks shift.
  //  - Images not decoded → blank logo slot.
  //  - Layout not yet settled after fonts/images → off-by-a-frame paint.
  // Also: html2canvas reliably draws PNG/JPEG <img> sources, but trips on
  // SVG <img> sources — those render blank. Pre-rasterize them to PNG data
  // URLs so html2canvas sees ordinary bitmaps.
  await waitForFontsReady();
  await waitForImagesReady(opts.node);
  await rasterizeSvgImages(opts.node);
  await waitForImagesReady(opts.node); // wait for the replaced data-URL src to decode
  await nextFrame();

  const canvas = await html2canvas(opts.node, {
    scale: opts.scale ?? 2,
    backgroundColor: opts.backgroundColor ?? '#ffffff',
    useCORS: true,
    logging: false,
    imageTimeout: 15000,
    // Avoid html2canvas's own foreignObject fallback — it's inconsistent
    // across browsers and can produce blank captures.
    foreignObjectRendering: false,
  });

  const format = opts.format ?? 'png';
  const backgroundColor = opts.backgroundColor ?? '#ffffff';
  let encoded = encodeCanvas(canvas, format, opts.quality ?? 0.92);
  if (opts.maxBytes && encoded.byteSize > opts.maxBytes) {
    encoded = shrinkToBudget(canvas, opts.maxBytes, backgroundColor);
  }

  return {
    dataUrl: encoded.dataUrl,
    base64: encoded.base64,
    mimeType: encoded.mimeType,
    width: canvas.width,
    height: canvas.height,
    byteSize: encoded.byteSize,
  };
}

interface EncodedImage {
  dataUrl: string;
  base64: string;
  mimeType: string;
  byteSize: number;
}

/** Encode a canvas to a data URL and derive its bare base64 + decoded size. */
function encodeCanvas(
  canvas: HTMLCanvasElement,
  format: 'png' | 'jpeg',
  quality: number,
): EncodedImage {
  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const dataUrl =
    format === 'jpeg' ? canvas.toDataURL(mimeType, quality) : canvas.toDataURL(mimeType);
  const base64 = dataUrl.split(',')[1] ?? '';
  // base64 length × 0.75 ≈ decoded byte size.
  return { dataUrl, base64, mimeType, byteSize: Math.floor(base64.length * 0.75) };
}

/**
 * Re-encode the captured card to fit under `maxBytes`. JPEG is far smaller than
 * PNG for photographic share cards, so we always switch to JPEG here, step the
 * quality down, and only then downscale — preserving as much resolution as the
 * budget allows.
 */
function shrinkToBudget(
  source: HTMLCanvasElement,
  maxBytes: number,
  backgroundColor: string,
): EncodedImage {
  let canvas = source;
  let quality = 0.9;
  let best = encodeCanvas(canvas, 'jpeg', quality);
  let guard = 0;
  while (best.byteSize > maxBytes && guard++ < 12) {
    if (quality > 0.55) {
      quality = Math.max(0.55, quality - 0.12);
    } else {
      // Min quality still too big — shrink ~15% and reset quality.
      canvas = downscaleCanvas(canvas, 0.85, backgroundColor);
      quality = 0.8;
    }
    best = encodeCanvas(canvas, 'jpeg', quality);
  }
  return best;
}

/** Downscale a canvas by `factor` onto an opaque background (JPEG has no alpha). */
function downscaleCanvas(
  source: HTMLCanvasElement,
  factor: number,
  backgroundColor: string,
): HTMLCanvasElement {
  const w = Math.max(1, Math.round(source.width * factor));
  const h = Math.max(1, Math.round(source.height * factor));
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (ctx) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, w, h);
  }
  return out;
}

/**
 * Wait for all CSS fonts the document references to be ready. Without this,
 * html2canvas can snapshot using fallback metrics, which subtly changes line
 * lengths and pill aspect ratios vs. the live DOM. Safe no-op on browsers
 * that don't support `document.fonts`.
 */
async function waitForFontsReady(): Promise<void> {
  const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
  if (fonts?.ready) {
    try {
      await fonts.ready;
    } catch {
      // Treat font-load failures the same as success — fall through and capture.
    }
  }
}

/** Resolve on the next animation frame so any pending layout work finishes. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * html2canvas renders PNG/JPEG `<img>` sources cleanly but draws SVG sources
 * as blank in many real-world cases (its internal SVG path is brittle around
 * external references, CSS, and embedded fonts). To avoid the broken-logo
 * symptom: find every SVG `<img>` inside `node`, draw it into a same-origin
 * canvas at the element's natural size, and swap its `src` to the resulting
 * PNG data URL. After this, html2canvas sees only ordinary bitmaps.
 *
 * Failures are swallowed per-image — if rasterization fails we leave the
 * original `<img>` in place and accept the same blank slot as before
 * (better than blocking the whole capture).
 */
async function rasterizeSvgImages(node: HTMLElement): Promise<void> {
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    images.map(async (img) => {
      const src = img.currentSrc || img.src;
      if (!isSvgSource(src)) return;
      try {
        const png = await svgToPngDataUrl(
          src,
          img.naturalWidth || img.clientWidth || 256,
          img.naturalHeight || img.clientHeight || 256,
        );
        if (png) {
          img.src = png;
        }
      } catch {
        // Leave the original SVG in place — html2canvas will draw a blank
        // slot, same as before this fix. Better than failing the share.
      }
    }),
  );
}

function isSvgSource(src: string): boolean {
  if (!src) return false;
  return /\.svg(\?|#|$)/i.test(src) || src.startsWith('data:image/svg');
}

/** Load an SVG via `<img>` (same-origin), draw to a canvas, return PNG data URL. */
async function svgToPngDataUrl(src: string, width: number, height: number): Promise<string | null> {
  const tmp = new Image();
  // Same-origin SVGs don't need this, but it's safe defense for any future
  // cross-origin asset that returns the right headers.
  tmp.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    tmp.onload = () => resolve();
    tmp.onerror = () => reject(new Error('svg load failed'));
    tmp.src = src;
  });

  // 2× upscale so the rasterized SVG stays sharp at the final 2× capture.
  const scale = 2;
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));

  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(scale, scale);
  ctx.drawImage(tmp, 0, 0, w, h);

  try {
    return canvas.toDataURL('image/png');
  } catch {
    // Canvas tainted — shouldn't happen for same-origin SVG, but bail safely.
    return null;
  }
}

/**
 * Wait for every `<img>` inside the node to finish loading (or fail). We
 * resolve on `error` too — a broken image shouldn't block the capture; the
 * rest of the prescription is more important than a missing logo. Caller
 * sees the same blank slot it would see in the live DOM, not a hang.
 */
async function waitForImagesReady(node: HTMLElement): Promise<void> {
  const images = Array.from(node.querySelectorAll('img'));
  if (images.length === 0) return;
  await Promise.all(
    images.map((img) => {
      // Already decoded.
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        // Defensive timeout — if the load event never fires (e.g. interrupted
        // navigation), don't block the share flow indefinitely.
        setTimeout(done, 8000);
      });
    }),
  );
}
